import axios from "axios";
import { getToken, removeToken } from "../utils/tokenUtils";

// Use environment-appropriate API URL
const getApiBaseUrl = () => {
  // Check for explicit environment variable first
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // In production or when not on localhost, use relative URLs
  if (
    process.env.NODE_ENV === "production" ||
    (typeof window !== "undefined" &&
      !window.location.hostname.includes("localhost"))
  ) {
    return "/api";
  }

  // Development default
  return "http://localhost:3000/api";
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token to requests
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error:", error);

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      removeToken();

      // Only redirect if not already on login page
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  },
);

// Timesheet API
export const timesheetAPI = {
  upload: (formData) =>
    api.post("/timesheet/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  getSummary: () => api.get("/timesheet/summary"),
  updateRecord: (data) => api.put("/timesheet/update", data),
};

// SOW API
export const sowAPI = {
  getAll: (params) => api.get("/sow", { params }),
  getSummary: (params) => api.get("/sow/summary", { params }),
  create: (data) => api.post("/sow", data),
  update: (id, data) => api.put(`/sow/${id}`, data),
  delete: (id) => api.delete(`/sow/${id}`),
  getMonths: () => api.get("/sow/months"),
};

// Forecast Revenue API (Budget forecast - 100% utilization, no backfill)
export const forecastRevenueAPI = {
  getAll: (params) => api.get("/forecast-revenue", { params }),
  create: (data) => api.post("/forecast-revenue", data),
  update: (id, data) => api.put(`/forecast-revenue/${id}`, data),
  delete: (id) => api.delete(`/forecast-revenue/${id}`),
  getMonths: () => api.get("/forecast-revenue/months"),
};

// Actuals API
export const actualsAPI = {
  getAll: (params) => api.get("/actuals", { params }),
  getById: (id) => api.get(`/actuals/${id}`),
  create: (data) => api.post("/actuals", data),
  update: (id, data) => api.put(`/actuals/${id}`, data),
  delete: (id) => api.delete(`/actuals/${id}`),
  getSummary: (params) => api.get("/actuals/summary", { params }),
  getGrouped: (params) => api.get("/actuals/grouped", { params }),
  getReporting: (params) => api.get("/actuals/reporting", { params }),
};

// Clients API
export const clientsAPI = {
  getAll: () => api.get("/clients"),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post("/clients", data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getSummary: (id, params) => api.get(`/clients/${id}/summary`, { params }),
};

// Resources API
export const resourcesAPI = {
  getAll: () => api.get("/resources"),
  getById: (id) => api.get(`/resources/${id}`),
  create: (data) => api.post("/resources", data),
  update: (id, data) => api.put(`/resources/${id}`, data),
  delete: (id) => api.delete(`/resources/${id}`),
  getSummary: (id, params) => api.get(`/resources/${id}/summary`, { params }),
  getUtilization: (id, params) =>
    api.get(`/resources/${id}/utilization`, { params }),
};

// Resource Types API
export const resourceTypesAPI = {
  getAll: () => api.get("/resource-types"),
  getById: (id) => api.get(`/resource-types/${id}`),
  create: (data) => api.post("/resource-types", data),
  update: (id, data) => api.put(`/resource-types/${id}`, data),
  delete: (id) => api.delete(`/resource-types/${id}`),
  getResources: (id) => api.get(`/resource-types/${id}/resources`),
  ensureExists: (data) => api.post("/resource-types/ensure-exists", data),
};

// Reports API
export const reportsAPI = {
  getSummary: (params) => api.get("/reports/summary", { params }),
  getUtilization: (params) => api.get("/reports/utilization", { params }),
  getRevenue: (params) => api.get("/reports/revenue", { params }),
  exportCSV: (params) =>
    api.get("/reports/export/csv", {
      params,
      responseType: "blob",
    }),
  getCharts: (params) => api.get("/reports/charts", { params }),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get("/users", { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post("/users", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getStats: () => api.get("/users/stats/overview"),
};

// Available Days API
export const availableDaysAPI = {
  getAll: (params) => api.get("/available-days", { params }),
  getById: (id) => api.get(`/available-days/${id}`),
  create: (data) => api.post("/available-days", data),
  update: (id, data) => api.put(`/available-days/${id}`, data),
  delete: (id) => api.delete(`/available-days/${id}`),
};

// Auth API
export const authAPI = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  logout: () => api.post("/auth/logout"),
  getProfile: () => api.get("/auth/profile"),
  verifyToken: () => api.get("/auth/verify-token"),
};

export default api;
